import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

// Register English locale once
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(countries as any).registerLocale(enLocale);

// Precompute a normalized name -> alpha2 map from official English names
const officialNames: Record<string, string> = countries.getNames('en', { select: 'official' }) as Record<string, string>;

function normalizeName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/-/g, ' ')
    .replace(/\bthe\b/g, '')
    .replace(/republic of /g, 'republic of ')
    .replace(/\s+/g, ' ')
    .trim();
}

const normalizedOfficialNameToA2: Record<string, string> = Object.entries(officialNames).reduce(
  (acc, [a2, name]) => {
    acc[normalizeName(name)] = a2;
    return acc;
  },
  {} as Record<string, string>
);

// Known aliases that differ from official names used in our `ALL_COUNTRIES`
const ALIASES: Record<string, string> = {
  'ivory coast': 'CI', // official: Côte d'Ivoire
  'cote d ivoire': 'CI',
  'democratic republic of congo': 'CD', // official: Democratic Republic of the Congo
  'dr congo': 'CD',
  'congo': 'CG', // assume Republic of the Congo when ambiguous
  'cape verde': 'CV', // official: Cabo Verde
  'swaziland': 'SZ', // official: Eswatini
  'eswatini': 'SZ',
  'sao tome and principe': 'ST',
  'tanzania': 'TZ', // official: Tanzania, United Republic of
  'iran': 'IR', // Islamic Republic of Iran
  'russia': 'RU', // Russian Federation
  'south korea': 'KR',
  'north korea': 'KP',
  'laos': 'LA', // Lao People's Democratic Republic
  'moldova': 'MD', // Republic of Moldova
  'palestine': 'PS', // State of Palestine
  'vatican city': 'VA',
  'bolivia': 'BO',
  'venezuela': 'VE',
  'syria': 'SY',
};

// Utility: Convert ISO 3166-1 alpha-2 code to emoji flag
export function isoCodeToFlagEmoji(isoCode: string): string {
  if (!isoCode || isoCode.length !== 2) return '';
  const upper = isoCode.toUpperCase();
  const codePoints = [...upper].map((char) => 0x1f1e6 + (char.charCodeAt(0) - 65));
  return String.fromCodePoint(...codePoints);
}

function toAlpha2(labelOrId: string): string | null {
  if (!labelOrId) return null;
  const raw = labelOrId.trim();
  if (raw.length === 2 && countries.isValid(raw.toUpperCase())) {
    return raw.toUpperCase();
  }

  // Direct library lookup first
  const byExact = countries.getAlpha2Code(raw, 'en');
  if (byExact) return byExact as string;

  const normalized = normalizeName(raw);
  if (ALIASES[normalized]) return ALIASES[normalized];

  const byNormalized = normalizedOfficialNameToA2[normalized];
  if (byNormalized) return byNormalized;

  return null;
}

/**
 * Returns an emoji flag for a country label or id (slug). Falls back gracefully.
 */
export function getFlagEmoji(countryLabelOrId: string | null | undefined): string {
  if (!countryLabelOrId) return '';
  const a2 = toAlpha2(countryLabelOrId);
  return a2 ? isoCodeToFlagEmoji(a2) : '';
}


